import { ALERT_TYPE, TKeys, XMP_RESPONSE } from 'utils/enums';
import { apiService } from 'libs/axios/services';
import { LocalStorageKeys, StorageValTypeOf } from '../hook/useLocalStorage';
import { apiInstance, BASE_URL } from 'libs/axios/axiosInstance';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { userSlice } from 'reducer/userReducer';
import { showAlert } from 'reducer/rootReducer';
import { t } from 'i18next';
import i18n from 'libs/i18n/i18n';
import { CHART_TYPE, DATA_LANGUAGE, HTTP_CODE, THRESHOLD_COLOR } from './constants';
import { environment } from 'environments/environment';
import {
    getPrintFormatLayout,
    PrintFormatType,
} from 'components/pages/print-setting/print-setting.data';
import api from 'libs/axios/api';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

export const getVersion = () => {
    return '1.0.0';
};

export const getKeyByValue = (object: any, value: any) => {
    return Object.keys(object).find((key) => object[key] === value);
};

export const getLocalStorageValue = <T extends LocalStorageKeys>(
    key: T,
): StorageValTypeOf<T> | null => {
    if (typeof window === 'undefined') return null;
    const item = window.localStorage.getItem(key as string);
    if (item === null) return null;
    try {
        return JSON.parse(item) as StorageValTypeOf<T>;
    } catch (error) {
        return item as StorageValTypeOf<T>;
    }
};

export const formatToSlug = (text: string) => {
    return text.toLowerCase().replace(/ /g, '_');
};

export const getMessageText = (FRE_CODE: string, ...param: any[]) => {
    if (param.length === 0) {
        return FRE_CODE;
    }

    const args = param;
    return FRE_CODE.replace(/\{(\d+)\}/g, function () {
        const newValue = args[arguments[1]];
        return newValue !== null ? newValue : `${arguments[1]}`;
    });
};

export const replaceParams = (
    url: string,
    params: Record<string, string | number | undefined>,
): string => {
    return Object.keys(params).reduce((acc, key) => acc.replace(key, String(params[key])), url);
};

export const setLocalStorageValue = <T extends LocalStorageKeys>(key: T, value: string) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key as string, value);
};

export const removeLocalStorageValue = <T extends LocalStorageKeys>(key: T) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key as string);
};

export const getListColumnTableFetcher = async (
    params: URLSearchParams,
): Promise<ApiResponse<IGetUserTableColumnRes>> => {
    return apiService.get<URLSearchParams, IGetUserTableColumnRes>(
        `${BASE_URL}/user-setting-table/`,
        params,
    );
};

export const createUpdateListColumnTableFetcher = async (
    body: ICreateUpdateUserTableColumnReq,
): Promise<ApiResponse<IGetUserTableColumnRes>> => {
    return apiService.post<ICreateUpdateUserTableColumnReq, IGetUserTableColumnRes>(
        `${BASE_URL}/user-setting-table/`,
        body,
    );
};

export const logout = createAsyncThunk('user/logout', async (_, { dispatch }) => {
    // Call API logout
    const res = await apiService.post<URLSearchParams, any>(`${BASE_URL}/auth/logout`);

    if (!res.success) return;

    dispatch(
        showAlert({
            status: true,
            message: t(TKeys.LBL050071),
            type: ALERT_TYPE.SUCCESS,
        }),
    );

    // Remove authParams to localStorage
    removeLocalStorageValue('authParams');

    // Clear user state
    dispatch(userSlice.actions.removeUser());

    // Return locale ja default
    await changeI18nLocale(DATA_LANGUAGE.JP.name);
});

export const redirecLogin = createAsyncThunk('user/logout', async (_, { dispatch }) => {
    // Call API logout
    await apiService.post<URLSearchParams, any>(`${BASE_URL}/auth/logout`);

    // Remove authParams to localStorage
    removeLocalStorageValue('authParams');

    // Clear user state
    dispatch(userSlice.actions.removeUser());

    // Return locale ja default
    await changeI18nLocale(DATA_LANGUAGE.JP.name);
});

export const getMessageResponse = (
    data: string | { message_id: string; message_param: (string | number)[] },
) => {
    if (typeof data === 'string') return data;
    const { message_id, message_param } = data;
    const message = t(message_id);
    const params = message_param.map((e) => (typeof e === 'string' ? t(e) : e));
    if (message_param.length === 0) {
        return message;
    }
    return message.replace(/\{(\d+)\}/g, (_, index) => {
        const idx = Number(index);
        return idx >= 0 && idx < params.length ? String(params[idx]) : `{${idx}}`;
    });
};

export const changeI18nLocale = async (lang: string) => {
    await i18n.init(
        {
            ...i18n.init,
            lng: lang,
            fallbackLng: lang,
        },
        () => {
            console.log('- i18n locale updated');
        },
    );
};

export const downloadPdf = (data: string, fileName = 'downloaded-document.pdf') => {
    const blob = new Blob([data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);

    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const generateAuthorityCode = (length = 8): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

export const downloadFile = ({
    byteArray,
    fileName,
    isCsv = false,
}: {
    byteArray: BlobPart;
    fileName: string;
    isCsv?: boolean;
}) => {
    try {
        // const byteArray = new Uint8Array(fileBuffer);
        const contentType = isCsv
            ? 'text/csv'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const extension = isCsv ? 'csv' : 'xlsx';
        const blob = new Blob([byteArray], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.${extension}`;

        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
    } catch (error) {
        console.log('[downloadFile]: ', error);
    }
};

function parsePrintResponse(xmlString: string) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml');

    const resultNode = xmlDoc.querySelector('result');
    if (!resultNode) return null;
    return resultNode.textContent;
}
export const printQRCode = async (
    data: any[],
    printFormatType: PrintFormatType,
    onComplete: (errors: string[]) => void,
    paramsBuilder: (item: any) => { [key: string]: any },
    isContract: boolean = true,
) => {
    const errors: string[] = [];

    const printLayout = getPrintFormatLayout(printFormatType);

    const printItem = async (item: any, formatArchiveUrl: string): Promise<boolean> => {
        const params = {
            __format_archive_url: formatArchiveUrl,
            __format_id_number: environment.formatIdNumber,
            ...paramsBuilder(item),
        };

        try {
            const response = await apiInstance.get(environment.urlDevicePrint, { params });
            const result = parsePrintResponse(response.data);
            return response.status === 200 && result === XMP_RESPONSE.SUCCESS;
        } catch (err) {
            return false;
        }
    };

    try {
        const connect = await apiInstance.get(environment.urlDevicePrint);
        if (connect.status !== HTTP_CODE.SUCCESS) {
            errors.push(t(TKeys.MSE00043));
            onComplete(errors);
            return;
        }

        const defaultFormatUrl = `${environment.formatArchiveUrl}${printLayout}`;

        let contractLayoutUrl =
            isContract && data[0]?.contract_code
                ? `${data[0].contract_code}/${printLayout}`
                : printLayout;

        var formatArchiveUrl;
        try {
            const req = await apiInstance.post(
                api.checkPresignedUrl,
                JSON.stringify({ file_path: contractLayoutUrl }),
            );
            const presignedUrl = req.data.data.presignedUrl;
            formatArchiveUrl = presignedUrl;
        } catch (error) {
            formatArchiveUrl = defaultFormatUrl;
        }

        for (let index = 0; index < data.length; index++) {
            const item = data[index];
            let success = await printItem(item, formatArchiveUrl);

            if (!success && index === 0 && formatArchiveUrl !== defaultFormatUrl) {
                formatArchiveUrl = defaultFormatUrl;
                success = await printItem(item, formatArchiveUrl);
            }

            if (!success) {
                errors.push(t(TKeys.MSE00044));
            }
        }
        onComplete(errors);
    } catch (error) {
        errors.push(t(TKeys.MSE00044));
        onComplete(errors);
    }
};

export const useMuiColorByPercent = () => {
    const threshold = useSelector((state: RootState) => state.user.threshold);
    return (value: number | string, type: string): string => {
        const valueN = Number(value);
        if (type === CHART_TYPE.TEMPERATURE) {
            if (valueN >= threshold.temperature) return THRESHOLD_COLOR.ERROR;
            return THRESHOLD_COLOR.SUCCESS;
        } else {
            if (valueN >= threshold.dist_level_3) return THRESHOLD_COLOR.ERROR;
            if (valueN >= threshold.dist_level_2) return THRESHOLD_COLOR.WARNING;
            if (valueN >= threshold.dist_level_1) return THRESHOLD_COLOR.INFO;
            return THRESHOLD_COLOR.SUCCESS;
        }
    };
};

